import React, { useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import AddNotedIcon from 'icons/add-notes.svg';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { useCreatePersonnelTrainingReportPdfMutation } from 'components/hooks/personnelTrainingReportPdfMutations';

import LoadingToast from 'components/application/LoadingToast';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useBreadBoard } from 'components/contexts/Toaster';
import AdditionalTrainingBar from 'components/training/AdditionalTrainingBar';
import AdditionalTrainingTable from 'components/training/AdditionalTrainingTable';
import RoleTrainingBar from 'components/training/RoleTrainingBar';
import RoleTrainingTable from 'components/training/RoleTrainingTable';
import TrainingReportSidePanel from 'components/training/TrainingReportSidePanel';

import { personnelCourseConnectionsRequirements } from 'components/helpers/resources/courses';
import {
  courseTrainingStatusOrdering,
  getTrainingStatus,
  groupTrainingByCourseId,
} from 'components/helpers/resources/training';
import { resourceShape } from 'components/helpers/serialisableResources';

export default function RoleTrainingTab({
  attachments,
  autoEnrolmentConfigurations,
  autoEnrolmentExclusions,
  bookers,
  courseCompanyRoles,
  courses,
  coursesMeta,
  creditInfoUnavailable,
  currentBookings,
  currentPersonnel,
  currentRegistrations,
  eLearningAllowance,
  eLearningCourses,
  hasTrainingEditableAccess,
  isArchived,
  onAddBooking,
  onAddELearningBooking,
  onAddRole,
  onBookingReminderClick,
  onCourseSelectChange,
  onCourseSelectError,
  onCreateAutoEnrolmentExclusion,
  onDeleteAutoEnrolmentExclusion,
  onDeleteCourseClick,
  onDeleteTrainingClick,
  onEditTrainingClick,
  onNewTrainingClick,
  onRemoveBooking,
  onShowBooking,
  onTrainingEvidenceProcessed,
  onUserCourseCreate,
  onUserCourseUpdate,
  personnelCourseConnectionRequirementPriorities,
  personnelId,
  role,
  roles,
  setModalBoxData,
  simpleUsers,
  training,
  trainingEvidenceProcessed,
  trainingStatusReport,
  userCourses,
}) {
  const { addInedibleToast, addToast, handleBurnToast, setToasts } =
    useBreadBoard();

  const currentActor = useCurrentActor();
  const queryClient = useQueryClient();

  const createPersonnelTrainingReportPdfMutation =
    useCreatePersonnelTrainingReportPdfMutation();

  const [isTrainingReportSidePanelOpen, setIsTrainingReportSidePanelOpen] =
    useState(false);

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'training.RoleTrainingTab',
  });

  const { t: i18nTTraining } = useTranslation('common', {
    keyPrefix: 'training',
  });

  const removeBurningToasts = () => {
    setToasts((currentToasts) =>
      currentToasts.filter((toast) => toast.props?.burnTimer != null),
    );
  };

  const generatedTrainingReportJobUrl =
    createPersonnelTrainingReportPdfMutation.data?.job_url;

  const { data: jobStatus, isError: isJobStatusError } = useQuery({
    enabled: !!generatedTrainingReportJobUrl,
    queryKey: [
      'personnel',
      'trainingReportPdfs',
      generatedTrainingReportJobUrl,
    ],
    queryFn: async () => {
      const response = await axios.get(generatedTrainingReportJobUrl);
      return response.data;
    },
    refetchInterval: (data) => (!data || !data.export_url ? 1000 : false),
    refetchIntervalInBackground: true,
    retry: false,
  });

  const jobStatusExportUrl = jobStatus?.export_url;

  const { data: exportStatusData, isError: isExportStatusError } = useQuery({
    enabled: !!jobStatusExportUrl,
    queryKey: ['personnel', 'trainingReportPdfs', jobStatusExportUrl],
    queryFn: async () => {
      const response = await axios.get(jobStatusExportUrl);
      return response.data;
    },
    refetchInterval: (data) => (!data || !data.url ? 1000 : false),
    refetchIntervalInBackground: true,
    retry: false,
  });

  const handleExportReset = () => {
    createPersonnelTrainingReportPdfMutation.reset();

    queryClient.removeQueries({
      queryKey: ['personnel', 'trainingReportPdfs'],
    });
  };

  if (exportStatusData?.url) {
    window.location.href = exportStatusData.url;
    removeBurningToasts();
    handleExportReset();
  }

  const handleCreateTrainingReportSubmit = ({
    additionalOptionsAndCoursesParams,
  }) => {
    const trainingReportParams = {
      ...additionalOptionsAndCoursesParams,
      company_role_id: role ? role.id : null,
    };

    createPersonnelTrainingReportPdfMutation.mutate(
      { personnelId: currentPersonnel.id, trainingReportParams },
      {
        onSuccess: () => {
          addToast(
            <LoadingToast
              message={i18nT('downloadToast')}
              onBurnToast={handleBurnToast}
            />,
          );
          setIsTrainingReportSidePanelOpen(false);
        },
        onError: () => {
          removeBurningToasts();
          addInedibleToast();
          handleExportReset();
        },
      },
    );
  };

  const handleOpenTrainingReportSidePanel = () => {
    setIsTrainingReportSidePanelOpen(true);
  };

  const hasPersonnelTrainingReportPdfErrored =
    createPersonnelTrainingReportPdfMutation.isError ||
    isJobStatusError ||
    isExportStatusError;

  const isPersonnelTrainingReportPdfGenerating =
    createPersonnelTrainingReportPdfMutation.isLoading ||
    (!!generatedTrainingReportJobUrl &&
      !hasPersonnelTrainingReportPdfErrored &&
      !exportStatusData?.url);

  const handleCloseTrainingReportSidePanel = () => {
    setIsTrainingReportSidePanelOpen(false);
  };

  const personnelRole = role || {
    id: 'blankRole',
    relationships: { courses: { data: [] } },
  };

  const tabCompanyRoleCourseIds =
    personnelRole.relationships.courses ?
      personnelRole.relationships.courses.data.map(
        (companyRoleCourse) => companyRoleCourse.id,
      )
    : [];
  const allCompanyRoleCourseIds = roles.reduce((acc, role) => {
    const ids = role.relationships.courses.data.map(
      (roleCourse) => roleCourse.id,
    );
    return [...acc, ...ids];
  }, []);
  const allUserCourseCourseIds = userCourses.map(
    (userCourse) => userCourse.relationships.course.data.id,
  );

  const nonAssociatedTraining = training.filter(
    (training) =>
      ![...allUserCourseCourseIds, ...allCompanyRoleCourseIds].includes(
        training.relationships.course.data.id,
      ),
  );
  const nonAssociatedTrainingCourseIds = nonAssociatedTraining.map(
    (training) => training.relationships.course.data.id,
  );

  const bookingCourseIds = currentBookings.map(
    (booking) => booking.relationships.course.data.id,
  );
  const tabCompanyRoleCourses = tabCompanyRoleCourseIds.map(
    (id) => courses[id],
  );
  const roleHasCourses = tabCompanyRoleCourses.length > 0;

  const additionalCourses = [
    ...new Set([
      ...allUserCourseCourseIds,
      ...allCompanyRoleCourseIds,
      ...nonAssociatedTrainingCourseIds,
      ...bookingCourseIds,
    ]),
  ]
    .filter((id) => !tabCompanyRoleCourseIds.includes(id))
    .map((id) => courses[id]);

  const additionalCourseIds = new Set(
    additionalCourses.map((course) => course.id),
  );

  const courseCompanyRolesForRole = courseCompanyRoles.filter(
    (ccr) => ccr.relationships.companyRole.data.id === role.id,
  );
  const filteredCourseCompanyRoles = courseCompanyRolesForRole.reduce(
    (acc, ccr) => {
      acc[ccr.relationships.course.data.id] = ccr;
      return acc;
    },
    {},
  );
  const courseIdsOptionalOnOtherRoles = new Set(
    courseCompanyRoles
      .filter(
        (ccr) =>
          !ccr.attributes.required &&
          ccr.relationships.companyRole.data.id !== personnelRole.id,
      )
      .map((ccr) => ccr.relationships.course.data.id),
  );

  if (
    currentActor.user.attributes.accessType === 'personnel' &&
    tabCompanyRoleCourses.length === 0 &&
    additionalCourses.length === 0
  ) {
    return (
      <div className='tw-flex tw-h-90 tw-flex-col tw-items-center tw-justify-center tw-rounded-md tw-border-1 tw-border-solid tw-border-grey-100 tw-bg-white'>
        <AddNotedIcon className='-tw-mt-3' height={92} width={92} />
        <div className='tw-mt-3 tw-text-center tw-text-l tw-font-semibold tw-text-grey-900'>
          {i18nTTraining('noTrainingAdded')}
        </div>
        <div className='tw-mt-4 tw-text-center tw-text-grey-700'>
          {i18nTTraining('contactManagerToRequestTraining')}
        </div>
      </div>
    );
  }

  const trainingByCourseId = groupTrainingByCourseId({ training });

  const coursesWithTrainingStatus = Object.values(courses || {}).reduce(
    (acc, course) => {
      const latestTraining =
        (trainingByCourseId[course.id] && trainingByCourseId[course.id][0]) ||
        undefined;

      const courseInRole = personnelRole.relationships.courses.data.find(
        (courseRef) => courseRef.id === course.id,
      );

      const courseCompanyRole =
        courseInRole ? filteredCourseCompanyRoles[course.id] : undefined;

      const userCourse = userCourses.find(
        (userCourse) => userCourse.relationships.course.data.id === course.id,
      );

      const personnelCourseConnectionRequirementPriority =
        personnelCourseConnectionRequirementPriorities?.[
          userCourse?.relationships.personnelCourseConnectionRequirementPriority
            .data?.id
        ];

      const personnelCourseConnectionRequirementPriorityUser =
        simpleUsers?.[
          personnelCourseConnectionRequirementPriority?.relationships.user.data
            ?.id
        ];

      const isAssignedToAnotherRole =
        !courseInRole && allCompanyRoleCourseIds.includes(course.id);
      const isOptionalOnAnotherRole = courseIdsOptionalOnOtherRoles.has(
        course.id,
      );

      const {
        isRequired,
        isRequiredByCourseCompanyRole,
        isRequiredByUserCourse,
      } = personnelCourseConnectionsRequirements({
        courseCompanyRole,
        isRequirementPriorityPresent:
          !!personnelCourseConnectionRequirementPriority,
        userCourse,
      });

      const orderingBand = courseTrainingStatusOrdering({
        course,
        isRequired,
        training: latestTraining,
      });

      let requiredSource;
      if (isRequiredByUserCourse) {
        requiredSource = 'UserCourse';
      } else if (isRequiredByCourseCompanyRole) {
        requiredSource = 'Role';
      }

      const trainingStatus = getTrainingStatus({
        training: latestTraining,
        course,
      });

      const isOptionalOnRole = !!(
        courseCompanyRole && !courseCompanyRole.attributes.required
      );

      acc.push({
        course,
        isOptionalOnRole,
        isOptionalOnAnotherRole,
        isRequired,
        isAssignedToAnotherRole,
        isRequiredByCourseCompanyRole,
        isRequiredByUserCourse,
        latestTraining,
        orderingBand,
        requiredSource,
        personnelCourseConnectionRequirementPriority,
        personnelCourseConnectionRequirementPriorityUser,
        trainingStatus,
        userCourse,
      });
      return acc;
    },
    [],
  );

  const coursesForTrainingReport = coursesWithTrainingStatus.map((course) => ({
    id: course.course.id,
    isDisabled:
      !course.isRequired &&
      !course.requiredSource &&
      ['missing', 'expired'].includes(course.trainingStatus) &&
      !course.isOptionalOnRole,
    isRequired: course.isRequired,
    name: course.course.attributes.name,
    requiredSource: course.requiredSource,
    trainingStatus: course.trainingStatus,
  }));

  const roleCoursesWithTrainingStatusInfo = coursesWithTrainingStatus.filter(
    (courseData) => tabCompanyRoleCourseIds.includes(courseData.course.id),
  );

  const additionalCoursesWithTrainingStatusInfo =
    coursesWithTrainingStatus.filter((courseData) =>
      additionalCourseIds.has(courseData.course.id),
    );

  return (
    <>
      <RoleTrainingBar
        isArchived={isArchived}
        isDownloadingReport={isPersonnelTrainingReportPdfGenerating}
        name={currentPersonnel.firstName}
        onAddRole={onAddRole}
        onDownloadClick={handleOpenTrainingReportSidePanel}
        onTrainingEvidenceProcessed={onTrainingEvidenceProcessed}
        personnelId={currentPersonnel.id}
        role={role}
        roleHasCourses={roleHasCourses}
        training={training}
        trainingEvidenceProcessed={trainingEvidenceProcessed}
        trainingStatusReport={trainingStatusReport}
      />
      {roleHasCourses && (
        <RoleTrainingTable
          attachments={attachments}
          autoEnrolmentConfigurations={autoEnrolmentConfigurations}
          autoEnrolmentExclusions={autoEnrolmentExclusions}
          bookers={bookers}
          courseCompanyRoles={filteredCourseCompanyRoles}
          coursesWithTrainingStatusInfo={roleCoursesWithTrainingStatusInfo}
          creditInfoUnavailable={creditInfoUnavailable}
          currentBookings={currentBookings}
          currentPersonnel={currentPersonnel}
          currentRegistrations={currentRegistrations}
          eLearningAllowance={eLearningAllowance}
          eLearningCourses={eLearningCourses}
          hasTrainingEditableAccess={hasTrainingEditableAccess}
          onAddBooking={onAddBooking}
          onAddELearningBooking={onAddELearningBooking}
          onBookingReminderClick={onBookingReminderClick}
          onCreateAutoEnrolmentExclusion={onCreateAutoEnrolmentExclusion}
          onDeleteAutoEnrolmentExclusion={onDeleteAutoEnrolmentExclusion}
          onDeleteTrainingClick={onDeleteTrainingClick}
          onEditTrainingClick={onEditTrainingClick}
          onNewTrainingClick={onNewTrainingClick}
          onRemoveBooking={onRemoveBooking}
          onShowBooking={onShowBooking}
          onUserCourseCreate={onUserCourseCreate}
          onUserCourseUpdate={onUserCourseUpdate}
          personnelId={personnelId}
          setModalBoxData={setModalBoxData}
          training={training}
        />
      )}
      {((isArchived && additionalCourses.length > 0) || !isArchived) && (
        <div
          className='tw-mt-4'
          data-element-name='additional-training-table-container'
        >
          {((currentActor.user.attributes.accessType === 'personnel' &&
            additionalCourses.length > 0) ||
            currentActor.user.attributes.accessType !== 'personnel') && (
            <AdditionalTrainingBar
              actionButtonsVisible={hasTrainingEditableAccess && !isArchived}
              additionalCoursesCount={additionalCourses.length}
              coursesMeta={coursesMeta}
              onCourseSelectChange={onCourseSelectChange}
              onCourseSelectError={onCourseSelectError}
              personnelId={currentPersonnel.id}
              roleHasCourses={roleHasCourses}
              roles={roles}
              training={training}
              userCourses={userCourses}
            />
          )}
          {additionalCourses.length > 0 && (
            <AdditionalTrainingTable
              attachments={attachments}
              autoEnrolmentConfigurations={autoEnrolmentConfigurations}
              autoEnrolmentExclusions={autoEnrolmentExclusions}
              bookers={bookers}
              coursesWithTrainingStatusInfo={
                additionalCoursesWithTrainingStatusInfo
              }
              creditInfoUnavailable={creditInfoUnavailable}
              currentBookings={currentBookings}
              currentPersonnel={currentPersonnel}
              currentRegistrations={currentRegistrations}
              eLearningAllowance={eLearningAllowance}
              eLearningCourses={eLearningCourses}
              hasTrainingEditableAccess={hasTrainingEditableAccess}
              onAddBooking={onAddBooking}
              onAddELearningBooking={onAddELearningBooking}
              onBookingReminderClick={onBookingReminderClick}
              onCreateAutoEnrolmentExclusion={onCreateAutoEnrolmentExclusion}
              onDeleteAutoEnrolmentExclusion={onDeleteAutoEnrolmentExclusion}
              onDeleteCourseClick={onDeleteCourseClick}
              onDeleteTrainingClick={onDeleteTrainingClick}
              onEditTrainingClick={onEditTrainingClick}
              onNewTrainingClick={onNewTrainingClick}
              onRemoveBooking={onRemoveBooking}
              onShowBooking={onShowBooking}
              onUserCourseCreate={onUserCourseCreate}
              onUserCourseUpdate={onUserCourseUpdate}
              personnelId={personnelId}
              roles={roles}
              setModalBoxData={setModalBoxData}
              training={training}
            />
          )}
        </div>
      )}
      <TrainingReportSidePanel
        courses={coursesForTrainingReport}
        isOpen={isTrainingReportSidePanelOpen}
        isReportGenerating={isPersonnelTrainingReportPdfGenerating}
        onClose={handleCloseTrainingReportSidePanel}
        onSubmit={handleCreateTrainingReportSubmit}
      />
    </>
  );
}

RoleTrainingTab.propTypes = {
  currentPersonnel: PropTypes.object.isRequired,
  currentBookings: PropTypes.array,
  currentRegistrations: PropTypes.array,
  training: PropTypes.array.isRequired,
  bookers: PropTypes.object,
  roles: PropTypes.array.isRequired,
  attachments: PropTypes.array.isRequired,
  role: PropTypes.object,
  courses: PropTypes.object,
  autoEnrolmentConfigurations: PropTypes.object,
  autoEnrolmentExclusions: PropTypes.object,
  eLearningCourses: PropTypes.object,
  eLearningAllowance: PropTypes.object,
  courseCompanyRoles: PropTypes.array,
  userCourses: PropTypes.array,
  hasTrainingEditableAccess: PropTypes.bool,
  creditInfoUnavailable: PropTypes.bool,
  onBookingReminderClick: PropTypes.func.isRequired,
  onShowBooking: PropTypes.func.isRequired,
  onRemoveBooking: PropTypes.func.isRequired,
  onCreateAutoEnrolmentExclusion: PropTypes.func,
  onDeleteAutoEnrolmentExclusion: PropTypes.func,
  isArchived: PropTypes.bool,
  onAddRole: PropTypes.func,
  trainingEvidenceProcessed: PropTypes.bool,
  onTrainingEvidenceProcessed: PropTypes.func,
  onNewTrainingClick: PropTypes.func,
  onEditTrainingClick: PropTypes.func,
  onDeleteTrainingClick: PropTypes.func,
  onAddBooking: PropTypes.func,
  onAddELearningBooking: PropTypes.func,
  setModalBoxData: PropTypes.func,
  personnelId: PropTypes.string,
  coursesMeta: PropTypes.object,
  onCourseSelectChange: PropTypes.func,
  onCourseSelectError: PropTypes.func,
  onUserCourseUpdate: PropTypes.func,
  onUserCourseCreate: PropTypes.func,
  onDeleteCourseClick: PropTypes.func,
  trainingStatusReport: PropTypes.shape({
    courses: PropTypes.arrayOf(resourceShape('courses')),
    status: PropTypes.string,
  }),
  personnelCourseConnectionRequirementPriorities: PropTypes.object,
  simpleUsers: PropTypes.object,
};

RoleTrainingTab.defaultProps = {
  userCourses: [],
};
